import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  FileText,
  Shield,
  Zap,
  User,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import * as _ from "lodash";
import axios from "axios";
import toast from "react-hot-toast";

interface IFormInput {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  emp_id: string;
}

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [fetchingInvitation, setFetchingInvitation] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
  }: any = useForm();

  const [searchParams] = useSearchParams();
  const invitedVerificationId = searchParams.get("invited_verification_id");

  useEffect(() => {
    const fetchInvitationInfo = async () => {
      if (!invitedVerificationId) return;

      try {
        setFetchingInvitation(true);
        const response = await axios.get<any>(
          `${
            import.meta.env.VITE_DMS_API
          }/dms-v2-srv/user-srv/users/invite/info/public/` +
            invitedVerificationId
        );

        if (response.data.success && response.data.data) {
          const { userinfo } = response.data.data;

          // Bind the response data to form fields
          setValue("firstName", userinfo.name.givenName || "");
          setValue("lastName", userinfo.name.familyName || "");
          setValue(
            "email",
            userinfo.emails[0]?.value || userinfo.userName || ""
          );
          setValue("emp_id", userinfo.emp_id || "");

          // Trigger validation for the populated fields
          trigger(["firstName", "lastName", "email"]);
        } else {
          toast.error("Failed to fetch invitation information");
        }
      } catch (error: any) {
        console.error("Error fetching invitation info:", error);
        toast.error(
          error?.response?.data?.message ||
            "Failed to fetch invitation information"
        );
      } finally {
        setFetchingInvitation(false);
      }
    };

    fetchInvitationInfo();
  }, [invitedVerificationId, setValue, trigger]);

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    setLoading(true);
    
    if (invitedVerificationId) {
      const form = document.createElement("form");
      form.action = `${import.meta.env.VITE_DMS_API
        }/dms-v2-srv/user-srv/register`;
      form.method = "POST";
      form.style.display = "none";

      const emailNameInput = document.createElement("input");
      emailNameInput.type = "hidden";
      emailNameInput.name = "email";
      emailNameInput.value = _.escape(data.email);
      form.appendChild(emailNameInput);

      const givenNameInput = document.createElement("input");
      givenNameInput.type = "hidden";
      givenNameInput.name = "given_name";
      givenNameInput.value = data.firstName;
      form.appendChild(givenNameInput);

      const familyNameInput = document.createElement("input");
      familyNameInput.type = "hidden";
      familyNameInput.name = "family_name";
      familyNameInput.value = data.lastName;
      form.appendChild(familyNameInput);

      const passwordInput = document.createElement("input");
      passwordInput.type = "hidden";
      passwordInput.name = "password";
      passwordInput.value = data.password;
      form.appendChild(passwordInput);

      const confirmPasswordInput = document.createElement("input");
      confirmPasswordInput.type = "hidden";
      confirmPasswordInput.name = "confirmPassword";
      confirmPasswordInput.value = data.confirmPassword;
      form.appendChild(confirmPasswordInput);

      const invitedVerificationIdInput = document.createElement("input");
      invitedVerificationIdInput.type = "hidden";
      invitedVerificationIdInput.name = "invite_id";
      invitedVerificationIdInput.value = invitedVerificationId || "";
      form.appendChild(invitedVerificationIdInput);

      form.onload = async () => {
        console.log("Form status:", form);

        try {
          if (form.status === 200) {
            toast.success("Registration successful. Please check your email.");
            navigate("/login");
          } else {
            console.error("Login failed. Status:", form.status);
            toast.error("Registration failed. Please try again.");
          }
        } catch (error) {
          console.error("Error during login:", error);
             const errorMessage = error?.response?.data?.error?.error_description ||
          error?.response?.data?.error?.message ||
          error?.response?.data?.error_description ||
          error?.response?.data?.message ||
          error?.message ||
          'Registration failed.';
          toast.error(errorMessage);
        } finally {
          setLoading(false);
          form.remove();
        }
      };
      document.body.appendChild(form);
      form.submit();
    }
    else {
      try {
        const config = {
          headers: { "Content-Type": "application/json" }
        };
        const payload = {
          // JSON for regular registration
          email: data.email,
          given_name: data.firstName,
          family_name: data.lastName,
          password: data.password,
        };
        const response = await axios.post(
          `${import.meta.env.VITE_DMS_API}/dms-v2-srv/user-srv/register`,
          payload,
          config
        );
        console.log("Registration response:", response);

        toast.success("Registration successful. Please check your email.");
        navigate("/register/success");
      } catch (error: any) {
        console.error("Error during registration:", error);
        const errorMessage = error?.response?.data?.error?.error_description ||
          error?.response?.data?.error?.message ||
          error?.response?.data?.error_description ||
          error?.response?.data?.message ||
          error?.message ||
          'Registration failed.';
        toast.error(
          errorMessage
        );
      } finally {
        setLoading(false);
      }
    }
  };

  // Feature list from the login page for consistency
  const features = [
    {
      icon: FileText,
      title: "DocIQ",
      description: "Organize and process documents with AI-powered automation",
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Bank-grade security with role-based access control",
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Process thousands of documents in minutes, not hours",
    },
  ];
 
  // Watch the password field for confirm password validation
  const password = watch("password");
 
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-800 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: "60px 60px",
        }}
      ></div>
 
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 lg:gap-12 relative z-10">
        {/* Left Side - Features */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="hidden lg:flex flex-col justify-center space-y-8"
        >
          <div className="text-white">
            <h1 className="text-4xl xl:text-4xl font-bold mb-4 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
              DocIQ
            </h1>
            <p className="text-xl text-blue-100 mb-12">
              Transform your document workflow with AI-powered automation and
              intelligent processing.
            </p>
          </div>
 
          <div className="space-y-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1, duration: 0.6 }}
                className="flex items-start space-x-4 p-4 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20"
              >
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <feature.icon className="h-6 w-6 text-blue-300" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-blue-100 text-sm">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
 
        {/* Right Side - Register Form */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center justify-center"
        >
          <div className="w-full max-w-md">
            <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-white/20">
              {/* Logo and Title */}
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Create an Account
                </h2>
                <p className="text-gray-600">
                  Get started with your new DMS account
                </p>
              </div>
 
              {/* Registration Form */}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* First and Last Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        {...register("firstName", {
                          required: "First name is required",
                        })}
                        onInput={(e) => {
                          e.currentTarget.value = e.currentTarget.value.replace(/[^A-Za-z\s]/g, "");
                        }}
                        className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                        placeholder="John"
                        disabled={fetchingInvitation}
                      />
                    </div>
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        {...register("lastName", {
                          required: "Last name is required",
                        })}
                        onInput={(e) => {
                          e.currentTarget.value = e.currentTarget.value.replace(/[^A-Za-z\s]/g, "");
                        }}
                        className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                        placeholder="Doe"
                        disabled={fetchingInvitation}
                      />
                    </div>
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>
                </div>
 
                {/* Email Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      autoComplete="off"
                      id="email"
                      type="email"
                      disabled={fetchingInvitation}
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: "Invalid email address",
                        },
                      })}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                      placeholder="Enter email"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.email.message}
                    </p>
                  )}
                </div>
 
                {/* Password Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      {...register("password", {
                        required: "Password is required",
                        minLength: {
                          value: 8,
                          message: "Password must be at least 8 characters",
                        },
                      })}
                      className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 [&::-ms-reveal]:hidden [&::-ms-clear]:hidden"
                      placeholder="Create a password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword ? (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.password.message}
                    </p>
                  )}
                </div>
 
                {/* Confirm Password Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      {...register("confirmPassword", {
                        required: "Please confirm password",
                        validate: (value) =>
                          value === password || "Passwords do not match",
                      })}
                      className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 [&::-ms-reveal]:hidden [&::-ms-clear]:hidden"
                      placeholder="Confirm password"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showConfirmPassword ? (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>
 
                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 transform hover:scale-[1.02]"
                >
                  {loading && (
                    <div className="inline-block h-4 w-4 me-2 mt-1 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
                  )}
                  REGISTER
                </button>
              </form>
 
              {/* Additional Links */}
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  Already have an account?{" "}
                  <button
                    onClick={() => navigate("/login")}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </div>
 
            {/* Mobile Features */}
            <div className="lg:hidden mt-8 space-y-4">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.6 }}
                  className="flex items-center space-x-3 text-white"
                >
                  <feature.icon className="h-5 w-5 text-blue-300" />
                  <span className="text-sm">{feature.title}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
 
export default Register;